import { useState, useCallback } from 'react';
import axios from 'axios';
import { get } from 'lodash';
import routes from '../../shared/routes';
import { LOGS_KEY } from '../../utils/constants';
import { logger } from '../../shared/logger';
import { retryAsync, isSectionEmpty } from '../../utils/retryRequest';

const safeExecuteCallback = (callback, callbackName = 'callback', params) => {
    try {
        callback?.(params);
    } catch (err) {
        logger.error(err, {
            [LOGS_KEY]: `Error while executing ${callbackName} in PersonalizedSectionFetcher`,
        });
    }
};

export const usePersonalizedData = ({
    appName,
    perseusName,
    onFetchSucceeded,
    onFetchFailed,
    recommendationsParams = {},
    withPolling = false,
    ...rest
}) => {
    const [personalizedData, setPersonalizedData] = useState<Record<string, any>>({});
    const [fetchStatus, setFetchStatus] = useState<{ isLoading: boolean; hasError?: boolean }>({
        isLoading: false,
        hasError: false,
    });

    const fetchPersonalizedData = useCallback(async (sectionName = null) => {
        setFetchStatus({ ...fetchStatus, isLoading: true });
        try {
            const fetchParams = sectionName
                ? {
                      ...recommendationsParams,
                      section_name: sectionName,
                  }
                : recommendationsParams;
            const requestPayload = { ...fetchParams, app_name: appName, perseus_name: perseusName };
            const sectionNameToUse = sectionName ?? rest.sectionName;
            const url = routes.by_section.fetcher(sectionNameToUse);
            let response;

            if (withPolling) {
                response = await retryAsync(() => axios.post(url, requestPayload), { sectionName: sectionNameToUse });
            } else {
                response = await axios.post(url, requestPayload);
            }

            if (isSectionEmpty(response, sectionNameToUse)) {
                safeExecuteCallback(onFetchFailed, 'onFetchFailed', {
                    message: 'Fetched invalid data',
                    fetchedSections: Object.keys(get(response.data, 'sections', {})),
                });
                setFetchStatus({ isLoading: false, hasError: true });
            } else {
                safeExecuteCallback(onFetchSucceeded, 'onFetchSucceeded', response.data);
                setPersonalizedData(response.data);
                setFetchStatus({ isLoading: false });
            }
        } catch (err: any) {
            const { sectionName, personalizedContent, isLoading, isLazy, showTranslatedUGC, rollouts } = rest;

            if (![403].includes(err.response?.status)) {
                logger.error(err, {
                    [LOGS_KEY]: 'Error while fetching personalized content in CSR with ajax',
                    componentProps: JSON.stringify({
                        appName,
                        perseusName,
                        recommendationsParams,
                        sectionName,
                        personalizedContent,
                        isLazy,
                        isLoading,
                        showTranslatedUGC,
                        rollouts,
                    }),
                });
            }

            safeExecuteCallback(onFetchFailed, 'onFetchFailed', err);
            setFetchStatus({ isLoading: false, hasError: true });
        }

        // We can't put the relevant props in the depts array because their reference is always changed
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { fetchPersonalizedData, personalizedData, fetchStatus };
};
